
import React from 'react';
import { Link } from 'react-router-dom';
import { EXAM_CATEGORIES } from '../constants';
import { LayoutGrid, ChevronRight, Search, Zap, Trophy, Heart, GraduationCap, Shield } from 'lucide-react';

const ExamCategories: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredCategories = EXAM_CATEGORIES.filter(cat =>
    cat.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Set document title for SEO
  React.useEffect(() => {
    document.title = 'PW Exam Categories Coupon Codes 2026 | Physics Wallah Discount';
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-400 text-xs font-bold mb-6 tracking-wide uppercase">
          <LayoutGrid size={14} className="fill-current" />
          PW Goal Selection 2026
        </div>
        <h1 className="text-5xl md:text-6xl font-black mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-500">PW Exam Categories Coupon Codes – Save on Physics Wallah Batches</h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
          Find your target exam from our comprehensive list of over 50+ categories and unlock exclusive PW savings.
        </p>

        {/* Search Bar */}
        <div className="max-w-xl mx-auto relative group">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-blue-400 transition-colors" size={20} />
          <input
            type="text"
            placeholder="Search your exam (e.g. UPSC, Nursing, BPSC...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-6 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 transition-all text-white placeholder:text-gray-600 font-bold"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat, i) => (
            <Link
              key={cat.slug}
              to={`/${cat.slug}`}
              className="group bg-[#111] border border-white/5 rounded-[32px] p-6 hover:border-blue-500/40 hover:bg-white/[0.04] transition-all flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="bg-white/5 p-3 rounded-2xl group-hover:bg-blue-600/10 group-hover:scale-110 transition-all">
                  <GraduationCap className="text-gray-500 group-hover:text-blue-400" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-300 group-hover:text-white transition-colors">{cat.label}</h3>
                  <p className="text-[10px] text-gray-600 uppercase font-black tracking-widest group-hover:text-blue-500/60">Verified 2026</p>
                </div>
              </div>
              <ChevronRight className="text-gray-700 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" size={18} />
            </Link>
          ))
        ) : (
          <div className="col-span-full py-20 text-center">
            <p className="text-gray-500 text-lg">No categories found matching "{searchTerm}"</p>
            <button onClick={() => setSearchTerm('')} className="mt-4 text-blue-400 font-bold hover:underline">Clear Search</button>
          </div>
        )}
      </div>

      {/* Popular Categories Highlight */}
      <div className="mt-24 bg-white/5 border border-white/10 rounded-[48px] p-12 text-center relative overflow-hidden mb-16">
        <Zap className="absolute -right-20 -top-20 w-80 h-80 text-blue-500/5 rotate-12 pointer-events-none" />
        <h2 className="text-3xl font-bold mb-8 relative z-10">Trending Goals This Month</h2>
        <div className="flex flex-wrap justify-center gap-4 relative z-10">
          {['pw-iit-jee-coupon-code', 'pw-neet-coupon-code', 'pw-upsc-coupon-code', 'pw-banking-coupon-code', 'pw-ssc-coupon-code', 'pw-gate-coupon-code'].map(slug => {
             const cat = EXAM_CATEGORIES.find(c => c.slug === slug);
             if (!cat) return null;
             return (
               <Link key={slug} to={`/${slug}`} className="px-6 py-3 bg-white/5 border border-white/10 rounded-full hover:bg-blue-600 hover:border-blue-600 transition-all font-bold text-sm">
                 {cat.label}
               </Link>
             );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExamCategories;
